import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useParams, useNavigate, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { MainLayout } from "layouts";
import { answerEdit, answerRegister } from "api/qna";
import Popup from "components/ui/Popup";
import Text from "components/ui/Text";
import Button from "components/entrance/Button";
import IconButton from "components/common/IconButton";
import Alert from "components/ui/Alert";
import Input from "components/ui/input/Text";
import DownArrow from "assets/icons/down-arrow-small.svg";
import iconSearch from "assets/icons/icon_search_blue.svg";
import iconCheck from "assets/icons/icon_check_blue.svg";
import defaultThumbnail from "assets/image/default_coach_thumbnail.svg";
import { updateCoachList } from "redux/slices/partner/partnerSlice";
import { setQnaList } from "redux/slices/qna/qnaSlice";

const QnaAnswer = () => {
  const { seqQna } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    commentSeq,
    coachName,
    content = "",
    dataLen = 0,
  } = location.state ?? {};
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState(content ?? "");
  const [searchText, setSearchText] = useState("");
  const [coach, setCoach] = useState({
    coachName: coachName ?? "강사선택",
    coachNumber: 0,
  });
  const { coachList, seqPartner, seqPartnerBranch } = useSelector(
    (state) => state.partner,
  );

  useEffect(() => {
    dispatch(updateCoachList());
  }, []);

  const handleChangeAnswer = (e) => {
    setAnswer(e.target.value);
  };
  const handleChangeSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleClickCoach = (coachName, seqPartnerCoach) => {
    setCoach({ coachName: coachName, coachNumber: seqPartnerCoach });
    setOpen(false);
  };

  const handleClickDone = () => {
    if (coach.coachName === "강사선택") {
      Alert("강사를 선택해주세요");
      return;
    }
    if (commentSeq) {
      answerEdit({
        seqPartner,
        seqPartnerBranch,
        uploadId: seqQna,
        commentId: commentSeq,
        commentContent: answer,
      })
        .then((data) => {
          navigate(-1);
        })
        .catch((e) => {});
      return;
    }
    answerRegister({
      uploadId: seqQna,
      seqPartnerCoach: coach.coachNumber,
      commentContent: answer,
    })
      .then((r) => {
        if (r) {
          navigate(-1);
          if (dataLen === 0) {
            dispatch(setQnaList({ seqQna, answerCompleteYn: "Y" }));
          }
        }
      })
      .catch((e) => {});
  };

  return (
    <MainLayout
      withBottomTab={false}
      withHeader={true}
      backgroundColor="white"
      headerProps={{
        padding: "0 20px 0 0", // right padding
        withBackButton: true,
        centerTitle: `답변${commentSeq ? "수정" : "작성"}하기`,
      }}
    >
      <Wrapper>
        <WrapperTop>
          <AnswerCoach onClick={commentSeq ? () => {} : () => setOpen(true)}>
            <Text lineHeight="24px">{coach.coachName}</Text>
            {commentSeq ? <></> : <IconButton src={DownArrow} />}
          </AnswerCoach>

          <TextArea>
            <StyledTextArea
              name="answer"
              placeholder="답변내용을 입력해주세요"
              maxLength={250}
              value={answer}
              onChange={handleChangeAnswer}
            />
          </TextArea>
        </WrapperTop>
        <WrapperBtm>
          <Button
            color={
              content !== answer && answer.trim().length !== 0
                ? "white"
                : "disable"
            }
            background={
              content !== answer && answer.trim().length !== 0
                ? "primary100"
                : "gray5"
            }
            onClick={
              content !== answer && answer.trim().length !== 0
                ? handleClickDone
                : () => {}
            }
          >
            {commentSeq ? "수정완료" : "작성완료"}
          </Button>
        </WrapperBtm>
      </Wrapper>

      <Popup open={open} onClose={setOpen}>
        <WrapperPopup>
          <Text
            fontWeight="600"
            lineHeight="20px"
            css={css`
              margin-bottom: 16px;
            `}
          >
            강사선택하기
          </Text>
          <InputBox>
            <Input
              placeholder="강사 이름으로 검색"
              value={searchText}
              onInput={handleChangeSearch}
            />
          </InputBox>
          <CoachBox>
            {coachList
              .filter((v) => v.coachName.indexOf(searchText) > -1)
              .map((v) => (
                <CoachCard
                  key={v.seqPartnerCoach}
                  onClick={() =>
                    handleClickCoach(v.coachName, v.seqPartnerCoach)
                  }
                  isChecked={coach.coachNumber === v.seqPartnerCoach}
                >
                  <img src={v.imgUrl || defaultThumbnail} />
                  <Text>{v.coachName}</Text>
                </CoachCard>
              ))}
          </CoachBox>
          <Button>확인</Button>
        </WrapperPopup>
      </Popup>
    </MainLayout>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: calc(100% - 50px);
  padding: 16px 20px 42px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const WrapperTop = styled.div`
  height: 100%;
`;
const AnswerCoach = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  width: 100%;
  height: 48px;
  border: 1px solid #e1e6ed;
  border-radius: 8px;
  color: #515458;
`;
const TextArea = styled.div`
  margin-top: 16px;
  height: calc(100% - 84px);
`;
const StyledTextArea = styled.textarea`
  resize: none;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.primaryTextColor};

  :focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.inputActive};
  }
`;

const WrapperBtm = styled.div`
  display: flex;
  button + button {
    margin-left: 10px;
    margin-top: 0px !important;
  }
`;

const WrapperPopup = styled.div`
  margin-bottom: 18px;
  input {
    padding-left: 3em;
    background: #f8f9fd url(${iconSearch}) no-repeat left 18px center / 1em;
    border-radius: var(--lineHeight);
    border: none;
  }
`;
const InputBox = styled.div`
  margin: 0 -4px;
`;

const CoachBox = styled.div`
  padding: 8px;
  margin-bottom: 24px;
  max-height: 35vh;
  overflow-y: auto;
`;
const CoachCard = styled.div`
  height: 44px;
  padding: 6px 0;
  display: flex;
  align-items: center;
  background: ${(props) =>
    props.isChecked
      ? `url(${iconCheck}) no-repeat right center / 1em`
      : "#fff"};
  img {
    width: 32px;
    height: 32px;
    margin-right: 10px;
    border-radius: 100%;
  }
`;

export default QnaAnswer;
